import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { Project } from '../../../../app/entities';
import { HasServerResponse } from '../../../../components/PanelContainer';
import { Alert } from '../../../../components/alert/Alert';
import { AddCover } from '../../../../components/add-file/add-cover';
import { PageLoader } from '../../../../components/page-loader/PageLoader';
import { formatPicture } from '../../../../core/services/image.service';
import { SelectionProvider } from '../../../../core/contexts/selection.context';
import {
  ProjectTemplateCategory,
  ProjectTemplateItem,
  ProjectTemplateType,
} from './project-template-item';

import styles from './desktop-create-project.modal.module.scss';
interface ModalProps {
  spaceId: string;
  onCancel: () => void;
  initialValues?: Project;
  onSubmit: (values: any) => void;
}

export const projectTemplates = [
  {
    category: ProjectTemplateCategory.Empty,
    value: ProjectTemplateType.Empty,
    backgroundImage: '',
    badge: undefined,
  },
  {
    category: ProjectTemplateCategory.Commercial,
    value: ProjectTemplateType.Commercial,
    backgroundImage: '/templates/commercial.jpeg',
    badge: 'Template',
  },
  {
    category: ProjectTemplateCategory.SocialMedia,
    value: ProjectTemplateType.SocialMedia,
    backgroundImage: '/templates/social-media.png',
    badge: 'Template',
  },
  {
    category: ProjectTemplateCategory.Documentary,
    value: ProjectTemplateType.Documentary,
    backgroundImage: '/templates/documentary.jpeg',
    badge: 'Template',
  },
  {
    category: ProjectTemplateCategory.ShortFiction,
    value: ProjectTemplateType.ShortFiction,
    backgroundImage: '/templates/fiction.png',
    badge: 'Template',
  },
  {
    category: ProjectTemplateCategory.Interview,
    value: ProjectTemplateType.Interview,
    backgroundImage: '/templates/interview.jpeg',
    badge: 'Template',
  },
  {
    category: ProjectTemplateCategory.Event,
    value: ProjectTemplateType.Event,
    backgroundImage: '/templates/event.jpeg',
    badge: 'Template',
  },
];

export const DesktopCreateProjectModal: React.FC<ModalProps> = ({
  spaceId,
  onCancel,
  onSubmit,
  initialValues,
}) => {
  const { t } = useTranslation('createProject');
  const [file, setFile] = useState<File | null>();
  const [projectTemplate, setProjectTemplate] = useState(ProjectTemplateType.Commercial);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<any>();
  const [fileError, setFileError] = useState(null);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .transform((value) => value.trim())
          .matches(/^(.*)?\S+(.*)?$/, t('common:fieldRequired'))
          .required(t('common:fieldRequired'))
          .max(32, t('common:fieldMax', { max: 32 })),
        client: yup.string().max(32, t('common:fieldMax', { max: 32 })),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      client: '',
      template: projectTemplate,
    },
  });

  const watchedName = watch('name');

  useEffect(() => {
    setValue('template', projectTemplate);

    const defaultDemoName = 'Breitling - History (Demo)';
    if (projectTemplate === ProjectTemplateType.Corporate) {
      if (!watchedName) {
        setValue('name', defaultDemoName);
      }
    } else {
      if (watchedName === defaultDemoName) {
        setValue('name', '');
      }
    }
  }, [projectTemplate]);

  const onHandleSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    setFileError(null);
    setIsLoading(true);

    let frame;
    if (file) {
      const options = { width: 640, height: 380 };
      frame = await formatPicture(file, options);
    }

    try {
      await onSubmit({
        spaceId,
        ...values,
        ...(frame && { file: frame }),
      });

      onCancel();
    } catch (e) {
      setServerError(e);
    }
  };

  return (
    <Dialog
      title={t('new_project')}
      onClose={onCancel}
      isMobileFill
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleSubmit(onHandleSubmit)}
          text="Create"
        />,
      ]}
    >
      <form className={styles['form']}>
        {serverError && serverError.response && (
          <HasServerResponse>
            <Alert text={serverError.response.data.message} />
          </HasServerResponse>
        )}

        <div className={styles['add-cover-wrapper']}>
          <AddCover
            setFieldValue={(_, file) => setFile(file)}
            errorMessage={fileError}
            file={file}
            sourceFile={initialValues?.avatar}
            acceptedFiles={['.png', '.jpeg']}
          />
        </div>

        <div className={styles['inputs-wrapper']}>
          <TextInput
            {...register('name')}
            errorMessage={errors.name?.message}
            label={t('name')}
            placeholder={t('name_placeholder')}
            required
          />

          <TextInput
            {...register('client')}
            name={'client'}
            label={t('client')}
            placeholder={t('client_placeholder')}
            autocomplete="false"
            errorMessage={errors.client?.message}
          />
        </div>
      </form>

      <div className={styles['project-templates-container']}>
        <p>Use a demo or a template to get started</p>

        <div className={styles['project-templates']}>
          <SelectionProvider items={projectTemplates.map((el) => el.value)}>
            {projectTemplates.map((template) => (
              <ProjectTemplateItem
                key={template.value}
                category={template.category}
                value={template.value as ProjectTemplateType}
                defaultValue={ProjectTemplateType.Commercial}
                badge={template.badge as 'Template' | 'Demo'}
                backgroundImage={template.backgroundImage}
                onSelect={() => setProjectTemplate(template.value)}
              />
            ))}
          </SelectionProvider>
        </div>
      </div>

      {isLoading && (
        <div className={styles['loading-overlay']}>
          <PageLoader />
        </div>
      )}
    </Dialog>
  );
};
